const SCROLL_ANIMATION_TRIGGER_CLASSNAME = "m-scroll-trigger";
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = "m-scroll-trigger--offscreen";
const SCROLL_ANIMATION_CANCEL_CLASSNAME = "m-scroll-trigger--cancel";

function updateElementVisibility(element, isVisible) {
  if (isVisible) {
    element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
  } else {
    element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
  }
}

function handleMobileChange(element) {
  const removeTriggerClass = () => element.classList.remove(SCROLL_ANIMATION_TRIGGER_CLASSNAME);

  if (MinimogTheme.config.mqlMobile || element.dataset.type === "fixed") {
    removeTriggerClass();
  }

  document.addEventListener("matchMobile", removeTriggerClass);
  document.addEventListener("unmatchMobile", removeTriggerClass);
}

function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const { isIntersecting, target } = entry;
    updateElementVisibility(target, isIntersecting);

    if (isIntersecting) {
      if (target.hasAttribute("data-cascade")) {
        target.setAttribute("style", `--animation-order: ${index};`);
      }
      observer.unobserve(target);
    }

    if (target.classList.contains("m-sidebar")) {
      handleMobileChange(target);
    }
  });
}

const observer = new IntersectionObserver(onIntersection, {
  rootMargin: "0px 0px -50px 0px",
});

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const elements = rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME);

  if (elements.length === 0 || isDesignModeEvent) return;

  Array.from(elements).forEach((element) => observer.observe(element));
}

document.addEventListener("DOMContentLoaded", () => initializeScrollAnimationTrigger());

if (Shopify.designMode) {
  const reinitialize = (event) => initializeScrollAnimationTrigger(event.target, true);
  document.addEventListener("shopify:section:load", reinitialize);
  document.addEventListener("shopify:section:reorder", () => initializeScrollAnimationTrigger(document, true));
}

/**
 * Premium GSAP Animations System
 * 
 * Features:
 * - Letter-by-letter heading animations
 * - Subheading animations (after heading completes)
 * - Button animations with scale and fade
 * - Works on all sections (hero, intro, regular)
 * - Smooth, premium, luxury feel
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    heading: {
      translateY: 20,
      opacity: { from: 0, to: 1 },
      duration: 0.8,
      stagger: 0.03,
      ease: 'power3.out'
    },
    subheading: {
      translateY: 30,
      opacity: { from: 0, to: 1 },
      duration: 0.6,
      delay: 0.3, // Delay after heading completes
      ease: 'power2.out'
    },
    button: {
      scale: { from: 0.95, to: 1 },
      opacity: { from: 0, to: 1 },
      duration: 0.5,
      delay: 0.2, // Delay after heading starts
      ease: 'power2.out'
    },
    triggerStart: 'top 85%'
  };

  let gsapLoaded = false;
  let animationsInitialized = false;

  /**
   * Split text into individual letter spans
   */
  function splitTextIntoSpans(element) {
    // Skip if already split
    if (element.querySelector('span[data-letter]')) {
      return element;
    }

    const text = element.textContent.trim();
    const words = text.split(/\s+/);
    element.innerHTML = '';

    words.forEach((word, wordIndex) => {
      const wordSpan = document.createElement('span');
      wordSpan.style.display = 'inline-block';
      wordSpan.style.whiteSpace = 'nowrap';

      // Split word into letters
      for (let i = 0; i < word.length; i++) {
        const letterSpan = document.createElement('span');
        letterSpan.setAttribute('data-letter', 'true');
        letterSpan.style.display = 'inline-block';
        letterSpan.textContent = word[i];
        wordSpan.appendChild(letterSpan);
      }

      element.appendChild(wordSpan);

      // Add space between words (except last word)
      if (wordIndex < words.length - 1) {
        const spaceSpan = document.createElement('span');
        spaceSpan.setAttribute('data-letter', 'true');
        spaceSpan.style.display = 'inline-block';
        spaceSpan.style.width = '0.25em';
        element.appendChild(spaceSpan);
      }
    });

    return element;
  }

  /**
   * Load GSAP library
   */
  function loadGSAP(callback) {
    if (typeof gsap !== 'undefined') {
      gsapLoaded = true;
      if (typeof ScrollTrigger === 'undefined') {
        loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js', function() {
          if (typeof ScrollTrigger !== 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
          }
          callback();
        });
      } else {
        if (typeof gsap.registerPlugin === 'function') {
          gsap.registerPlugin(ScrollTrigger);
        }
        callback();
      }
      return;
    }

    loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js', function() {
      loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js', function() {
        if (typeof ScrollTrigger !== 'undefined') {
          gsap.registerPlugin(ScrollTrigger);
        }
        gsapLoaded = true;
        callback();
      });
    });
  }

  /**
   * Load script dynamically
   */
  function loadScript(src, callback) {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = callback;
    script.onerror = function() {
      console.error('Failed to load script:', src);
    };
    document.head.appendChild(script);
  }

  /**
   * Animate heading with letter-by-letter effect
   */
  function animateHeading(heading, container, masterTimeline) {
    // Split text into spans
    splitTextIntoSpans(heading);
    
    const letterSpans = heading.querySelectorAll('[data-letter]');
    if (letterSpans.length === 0) return null;

    // Set initial state
    gsap.set(letterSpans, {
      opacity: CONFIG.heading.opacity.from,
      y: CONFIG.heading.translateY
    });

    // Calculate total duration for heading animation
    const totalDuration = CONFIG.heading.duration + (letterSpans.length * CONFIG.heading.stagger);

    // If master timeline exists, add to it
    if (masterTimeline) {
      masterTimeline.to(letterSpans, {
        opacity: CONFIG.heading.opacity.to,
        y: 0,
        duration: CONFIG.heading.duration,
        stagger: CONFIG.heading.stagger,
        ease: CONFIG.heading.ease
      }, 0); // Start at position 0 in master timeline
      return totalDuration;
    }

    // Otherwise create standalone timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container || heading,
        start: CONFIG.triggerStart,
        once: true,
        toggleActions: 'play none none none'
      }
    });

    tl.to(letterSpans, {
      opacity: CONFIG.heading.opacity.to,
      y: 0,
      duration: CONFIG.heading.duration,
      stagger: CONFIG.heading.stagger,
      ease: CONFIG.heading.ease
    });

    return totalDuration;
  }

  /**
   * Animate subheading (after heading completes)
   */
  function animateSubheading(subheading, headingDuration, container, masterTimeline) {
    // Set initial state
    gsap.set(subheading, {
      opacity: CONFIG.subheading.opacity.from,
      y: CONFIG.subheading.translateY
    });

    // Calculate start position (after heading + delay)
    const startPosition = headingDuration ? headingDuration + CONFIG.subheading.delay : CONFIG.subheading.delay;

    // If master timeline exists, add to it
    if (masterTimeline) {
      masterTimeline.to(subheading, {
        opacity: CONFIG.subheading.opacity.to,
        y: 0,
        duration: CONFIG.subheading.duration,
        ease: CONFIG.subheading.ease
      }, startPosition);
      return;
    }

    // Otherwise create standalone timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container || subheading,
        start: CONFIG.triggerStart,
        once: true,
        toggleActions: 'play none none none'
      }
    });

    tl.to(subheading, {
      opacity: CONFIG.subheading.opacity.to,
      y: 0,
      duration: CONFIG.subheading.duration,
      ease: CONFIG.subheading.ease
    }, startPosition);
  }

  /**
   * Animate button with scale and fade
   */
  function animateButton(button, headingDuration, container, masterTimeline) {
    // Set initial state
    gsap.set(button, {
      opacity: CONFIG.button.opacity.from,
      scale: CONFIG.button.scale.from
    });

    // Calculate start position (after heading starts + delay)
    const startPosition = headingDuration ? CONFIG.heading.duration * 0.3 + CONFIG.button.delay : CONFIG.button.delay;

    // If master timeline exists, add to it
    if (masterTimeline) {
      masterTimeline.to(button, {
        opacity: CONFIG.button.opacity.to,
        scale: CONFIG.button.scale.to,
        duration: CONFIG.button.duration,
        ease: CONFIG.button.ease
      }, startPosition);
      return;
    }

    // Otherwise create standalone timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container || button,
        start: CONFIG.triggerStart,
        once: true,
        toggleActions: 'play none none none'
      }
    });

    tl.to(button, {
      opacity: CONFIG.button.opacity.to,
      scale: CONFIG.button.scale.to,
      duration: CONFIG.button.duration,
      ease: CONFIG.button.ease
    }, startPosition);
  }

  /**
   * Process a container section for animations
   */
  function processSection(section) {
    // Find heading elements (various selectors)
    const headings = section.querySelectorAll(
      '.m-section__heading, .m-hero__title, .m-richtext__title, .cutout-hero__title, h1, h2, h3'
    );

    // Find subheading elements
    const subheadings = section.querySelectorAll(
      '.m-section__subheading, .m-hero__subtitle, .m-richtext__subtitle, .cutout-hero__subtitle'
    );

    // Find button elements
    const buttons = section.querySelectorAll(
      '.m-button, .m-richtext__button, .m-section__button a, button.m-button, a.m-button'
    );

    // Group elements by their closest container
    const headingGroups = new Map();
    
    // Helper function to get group key
    function getGroupKey(element, section) {
      const container = element.closest('.m-section, .m-hero, .m-hero__content, .m-section__header, .m-richtext, .cutout-hero__content, .m-hero__inner') || section;
      // Use ID if available, otherwise use a combination of container and position
      if (container.id) {
        return container.id;
      }
      // Create a unique key based on container and element position
      const rect = container.getBoundingClientRect();
      return `${container.tagName}-${Math.round(rect.top)}-${Math.round(rect.left)}`;
    }
    
    headings.forEach(heading => {
      const groupKey = getGroupKey(heading, section);
      
      if (!headingGroups.has(groupKey)) {
        const container = heading.closest('.m-section, .m-hero, .m-hero__content, .m-section__header, .m-richtext, .cutout-hero__content, .m-hero__inner') || section;
        headingGroups.set(groupKey, {
          container: container,
          headings: [],
          subheadings: [],
          buttons: []
        });
      }
      
      headingGroups.get(groupKey).headings.push(heading);
    });

    // Group subheadings by same containers
    subheadings.forEach(subheading => {
      const groupKey = getGroupKey(subheading, section);
      
      if (!headingGroups.has(groupKey)) {
        const container = subheading.closest('.m-section, .m-hero, .m-hero__content, .m-section__header, .m-richtext, .cutout-hero__content, .m-hero__inner') || section;
        headingGroups.set(groupKey, {
          container: container,
          headings: [],
          subheadings: [],
          buttons: []
        });
      }
      
      headingGroups.get(groupKey).subheadings.push(subheading);
    });

    // Group buttons by same containers
    buttons.forEach(button => {
      const groupKey = getGroupKey(button, section);
      
      if (!headingGroups.has(groupKey)) {
        const container = button.closest('.m-section, .m-hero, .m-hero__content, .m-section__header, .m-richtext, .cutout-hero__content, .m-hero__inner') || section;
        headingGroups.set(groupKey, {
          container: container,
          headings: [],
          subheadings: [],
          buttons: []
        });
      }
      
      headingGroups.get(groupKey).buttons.push(button);
    });

    // Animate each group
    headingGroups.forEach(group => {
      // Skip if no elements to animate
      if (group.headings.length === 0 && group.subheadings.length === 0 && group.buttons.length === 0) {
        return;
      }

      // Create master timeline for this group
      const masterTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: group.container,
          start: CONFIG.triggerStart,
          once: true,
          toggleActions: 'play none none none'
        }
      });

      let headingDuration = 0;

      // Animate headings first
      if (group.headings.length > 0) {
        group.headings.forEach((heading, index) => {
          const duration = animateHeading(heading, group.container, masterTimeline);
          if (index === 0 && duration) {
            headingDuration = duration;
          }
        });
      }

      // Animate subheadings after headings (or immediately if no headings)
      if (group.subheadings.length > 0) {
        group.subheadings.forEach(subheading => {
          animateSubheading(subheading, headingDuration, group.container, masterTimeline);
        });
      }

      // Animate buttons (can start during heading animation)
      if (group.buttons.length > 0) {
        group.buttons.forEach(button => {
          animateButton(button, headingDuration, group.container, masterTimeline);
        });
      }
    });
  }

  /**
   * Initialize animations
   */
  function initAnimations() {
    if (animationsInitialized) return;
    animationsInitialized = true;

    // Find all sections
    const sections = document.querySelectorAll(
      '.m-section, .m-hero-section, section[id^="m-section"], section[id^="m-hero-section"]'
    );

    // Process each section
    sections.forEach(section => {
      processSection(section);
    });

    // Also process standalone elements outside sections
    const standaloneHeadings = document.querySelectorAll(
      '.m-section__heading:not(.m-section .m-section__heading), .m-hero__title:not(.m-hero .m-hero__title)'
    );
    
    if (standaloneHeadings.length > 0) {
      standaloneHeadings.forEach(heading => {
        const container = heading.closest('body') || document.body;
        processSection(container);
      });
    }
  }

  /**
   * Wait for DOM and GSAP, then initialize
   */
  function init() {
    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        loadGSAP(initAnimations);
      });
    } else {
      loadGSAP(initAnimations);
    }

    // Reinitialize on Shopify section load (for theme editor)
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', function(event) {
        animationsInitialized = false;
        setTimeout(() => {
          if (gsapLoaded) {
            initAnimations();
          } else {
            loadGSAP(initAnimations);
          }
        }, 100);
      });
    }
  }

  // Start initialization
  init();
})();
